<template>
  <v-container
    fluid
    class="release-summary-page pa-0"
  >
    <v-row class="no-gutters">
      <v-col>
        <v-card class="py-9 px-12 elevation-0">
          <v-card-text class="font-weight-medium">
            <docs-hero-banner
              eyebrow="Release Hub"
              title="Release Summary"
              description="Executive summary covering major platform changes shipped from late 2025 through April 2026."
              :meta-items="heroMetaItems"
            />

            <docs-callout
              title="Use this page for fast alignment"
              color="info"
              :icon="['fas', 'info-circle']"
            >
              This page is the quick-read version of release communication. Use
              Full Release Notes when you need the full historical record.
            </docs-callout>

            <docs-section-card
              title="Platform and Workflow Highlights"
              description="These are the biggest shifts leaders and internal teams are most likely to ask about."
              :icon="['fas', 'chart-line']"
            >
              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Projects Platform Expanded Into a Full Operational Workspace
                </h4>
                <p>
                  The Projects experience grew from basic project metadata management into a more complete operational
                  workspace with project status, type, and platform management, richer admin tooling, improved create and
                  edit flows, and stronger linkage support across Firestore, API, and spreadsheet-backed workflows.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Time, Delivery, and Organization Views Were Modernized
                </h4>
                <p>
                  Core internal workflows received major UI and architecture upgrades including Time view layouts,
                  Delivery view refactors, grouping and filtering improvements, sticky data tables, more resilient query
                  parameter behavior, and component-level state patterns that make the app easier to maintain.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Onboarding and Help Surfaces Became Product Features
                </h4>
                <p>
                  Dashboard quick-start guidance, contextual help, guided tours, docs deep-links, and QA checklists are
                  now part of the product workflow instead of scattered support artifacts. This improves first-session
                  orientation for users and gives internal teams clearer validation coverage.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Reliability, Security, and Auth Diagnostics Were Hardened
                </h4>
                <p>
                  Protected-route startup is more reliable, authentication failures are easier to diagnose, internal
                  developer tools are more tightly gated, and several security and configuration changes reduced risk in
                  both runtime and operational tooling.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Release Engineering and Project Audit Tooling Matured Significantly
                </h4>
                <p>
                  Release note generation, semver metadata tracking, deployment documentation, and project-data audit
                  packets are now more structured and repeatable. This release window also introduced refreshed audit
                  outputs and remediation proposal artifacts for project-data cleanup work.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Docs Navigation Clarified for Release Notes and Release Kit
                </h4>
                <p>
                  The Docs sidebar now labels Release Notes clearly and keeps both
                  <code>/docs/releases</code> and <code>/docs/release-notes</code> working.
                  Internal users also get a dedicated Release Kit sidebar item for communication templates.
                </p>
              </div>
            </docs-section-card>

            <div class="mt-5">
              <v-btn
                outlined
                class="text-none"
                to="/docs/release-notes"
              >
                <font-awesome-icon
                  :icon="['fas', 'book-open']"
                  class="mr-2"
                  size="sm"
                />
                Open Full Release Notes
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: 'ReleaseSummary',
  components: {
    DocsCallout: () => import('@/docs/components/DocsCallout.vue'),
    DocsHeroBanner: () => import('@/docs/components/DocsHeroBanner.vue'),
    DocsSectionCard: () => import('@/docs/components/DocsSectionCard.vue'),
  },
  computed: {
    heroMetaItems() {
      return [
        { label: 'Audience: Executives and stakeholders', icon: ['fas', 'user-tie'] },
        { label: 'Scope: Since v4.72.3', icon: ['fas', 'calendar-alt'] },
        { label: 'Format: High-level summary', icon: ['fas', 'list'] },
      ]
    },
  },
}
</script>

<style lang="scss" scoped>
.release-summary-page {
  max-width: 1200px;
  margin: 0 auto;
}

.release-badge {
  display: inline-block;
  padding: 1px 8px;
  margin-right: 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  vertical-align: middle;
  line-height: 18px;
}

.release-entry {
  margin-bottom: 16px;

  h4 {
    margin-bottom: 4px;
    font-size: 14px;
    line-height: 1.6;
  }

  p {
    font-size: 13px;
    line-height: 1.55;
    margin-bottom: 8px;
  }

  code {
    font-size: 12px;
    padding: 1px 5px;
    border-radius: 3px;
    background-color: rgba(0, 0, 0, 0.05);
    color: #055289;
  }
}
</style>
